import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as formulationsApi from '../api/formulationsApi';
import * as broadcastsApi from '../api/broadcastsApi';
import { EmptyState, ErrorBanner, Modal, Pagination, TableSkeleton } from './ui';
import { FormulationDetailModal } from './FormulationDetail';
import { CartUser, CartUsers } from '../types/formulation';
import { CustomRecipient } from '../types/broadcast';
import { formatDate, formatDateTime } from '../utils/format';

/**
 * The people behind "Added to cart": everyone holding something they never
 * bought — a generated formula or plain catalogue stock, tagged per row.
 *
 * The list is not date-filtered, unlike the funnel bar that opens it. That is
 * deliberate — a bag abandoned four months ago is the one worth a phone call,
 * and scoping the list to the chart's window would hide it.
 */
export function CartUsersModal({ onClose }: { onClose: () => void }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CartUsers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formulationId, setFormulationId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Selection is keyed by USER, not by row. Someone holding two formulas appears
  // twice and must still be one message — ticking either row selects the person,
  // and the value carries the name/phone/cluster the composer will send with.
  // Held across pages: a selection made on page 1 survives paging to page 2.
  const [selected, setSelected] = useState<Map<string, CustomRecipient>>(new Map());

  const toggle = (u: CartUser) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(u.user_id)) next.delete(u.user_id);
      else {
        next.set(u.user_id, {
          // The greeting name is decided server-side (broadcast.ts's
          // resolveBroadcastName turns 'TPC User' and blanks into "there"), so
          // the raw display_name is passed through rather than second-guessed.
          name: u.display_name?.trim() || '',
          phone: u.phone ?? '',
          // `label`, never `product_name` — a formula's product_name carries
          // the recipient's own name and must not reach a template.
          cluster: u.label,
        });
      }
      return next;
    });
  };

  // Rows for the page currently on screen, so the header checkbox acts on what
  // the admin can actually see. Unreachable rows are never included.
  const selectableOnPage = useMemo(
    () => (data?.users ?? []).filter((u) => u.has_phone),
    [data]
  );
  const allOnPageSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((u) => selected.has(u.user_id));

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) {
        for (const u of selectableOnPage) next.delete(u.user_id);
      } else {
        for (const u of selectableOnPage) {
          if (next.has(u.user_id)) continue;
          next.set(u.user_id, {
            name: u.display_name?.trim() || '',
            phone: u.phone ?? '',
            cluster: u.label,
          });
        }
      }
      return next;
    });
  };

  // Hand the picked contacts to the campaign composer as a 'custom' send, so it
  // still goes through broadcast_campaigns / broadcast_recipients and shows up in
  // history with a retry, exactly like a full-segment send.
  const messageSelected = () => {
    navigate('/broadcasts', { state: { seedRecipients: Array.from(selected.values()) } });
  };

  // Keep this selection for next time. Saved groups snapshot the numbers, so a
  // group built here still sends to the same people once these bags are bought
  // out of and the rows drop off this list.
  const [savingGroup, setSavingGroup] = useState(false);
  const [groupMsg, setGroupMsg] = useState('');

  const saveAsGroup = async () => {
    const name = window.prompt('Name this group', '');
    if (!name || !name.trim()) return;
    setSavingGroup(true);
    setGroupMsg('');
    try {
      const res = await broadcastsApi.createGroup({
        name: name.trim(),
        members: Array.from(selected.values()),
      });
      setGroupMsg(`Saved "${name.trim()}" — ${res.memberCount} contact${res.memberCount === 1 ? '' : 's'}.`);
    } catch (err) {
      setGroupMsg(apiErrorMessage(err));
    } finally {
      setSavingGroup(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await formulationsApi.getCartUsers({ page }));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const total = data?.pagination.total ?? 0;

  return (
    <>
      <Modal
        open
        title={`Users with items in cart${total ? ` — ${total}` : ''}`}
        onClose={onClose}
        size="xl"
        bodyClassName=""
      >
        {error ? (
          <div className="p-5">
            <ErrorBanner message={error} onRetry={load} />
          </div>
        ) : loading && !data ? (
          <TableSkeleton rows={5} />
        ) : !data || data.users.length === 0 ? (
          <EmptyState
            message="Nobody is holding an unbought bag"
            hint="Everyone who added something either bought it or emptied their bag"
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th w-10">
                      <input
                        type="checkbox"
                        aria-label="Select everyone on this page"
                        className="h-4 w-4 accent-gold"
                        checked={allOnPageSelected}
                        disabled={selectableOnPage.length === 0}
                        onChange={toggleAllOnPage}
                      />
                    </th>
                    <th className="th">User Name</th>
                    <th className="th">Email</th>
                    <th className="th">In Their Bag</th>
                    <th className="th">Added to Cart</th>
                    <th className="th">Last Active</th>
                    <th className="th">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {data.users.map((u) => (
                    // A user holding two things is two rows, so the key has to
                    // carry both. A catalogue row has no formulation_id, so the
                    // product name stands in — a user can hold several.
                    <tr
                      key={`${u.user_id}-${u.formulation_id ?? u.product_name ?? 'item'}`}
                      className="transition-colors hover:bg-edge/20"
                    >
                      <td className="td">
                        {u.has_phone ? (
                          <input
                            type="checkbox"
                            aria-label={`Select ${u.display_name?.trim() || 'this user'}`}
                            className="h-4 w-4 accent-gold"
                            checked={selected.has(u.user_id)}
                            onChange={() => toggle(u)}
                          />
                        ) : (
                          <span className="whitespace-nowrap text-xs text-sub/60" title="No phone number on file">
                            no phone
                          </span>
                        )}
                      </td>
                      <td className="td">
                        <Link to={`/users/${u.user_id}`} className="font-medium text-gold hover:underline">
                          {u.display_name?.trim() || 'Unnamed'}
                        </Link>
                      </td>
                      <td className="td text-sub">{u.email || u.phone || '—'}</td>
                      <td className="td">
                        {u.label || '—'}
                        {u.item_type === 'catalogue' && (
                          <span className="ml-2 rounded-full border border-edge px-1.5 py-0.5 text-[10px] text-sub">
                            stock
                          </span>
                        )}
                      </td>
                      <td className="td text-sub">{formatDateTime(u.added_at)}</td>
                      <td className="td text-sub">{formatDate(u.last_active)}</td>
                      <td className="td">
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Never a paid order — paying takes the row off this
                              list — so this is the abandoned checkout, when
                              there was one at all. */}
                          {u.order_id ? (
                            <Link to={`/orders/${u.order_id}`} className="btn-ghost !px-2.5 !py-1 !text-xs">
                              View Order
                            </Link>
                          ) : (
                            <span className="text-xs text-sub/60">No order</span>
                          )}
                          {u.formulation_id && (
                            <button
                              type="button"
                              className="btn-outline !px-2.5 !py-1 !text-xs"
                              onClick={() => setFormulationId(u.formulation_id as string)}
                            >
                              View Formulation
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={data.pagination.page} totalPages={data.pagination.pages} onPage={setPage} />
            {selected.size > 0 && (
              <div className="flex flex-wrap items-center gap-3 border-t border-edge px-5 py-3">
                <button type="button" className="btn-gold !px-3 !py-1.5 !text-xs" onClick={messageSelected}>
                  Message selected
                </button>
                <span className="text-xs text-sub">
                  {selected.size} {selected.size === 1 ? 'person' : 'people'} selected — one message each, however
                  many formulas they hold.
                </span>
                <button
                  type="button"
                  className="btn-outline !px-3 !py-1.5 !text-xs"
                  onClick={saveAsGroup}
                  disabled={savingGroup}
                >
                  Save as group
                </button>
                <button type="button" className="btn-ghost !px-2.5 !py-1 !text-xs" onClick={() => setSelected(new Map())}>
                  Clear
                </button>
                {groupMsg && <span className="text-xs text-sub">{groupMsg}</span>}
              </div>
            )}
            <p className="border-t border-edge px-5 py-3 text-xs text-sub">
              Everyone here has something sitting in their cart — a made-to-order formula or
              catalogue stock — and has never completed a purchase. Not limited to the date range
              above: an old abandoned cart is the point.
            </p>
          </>
        )}
      </Modal>

      {/* Stacks over the list: closing it returns to the same page of rows. */}
      {formulationId && (
        <FormulationDetailModal id={formulationId} onClose={() => setFormulationId(null)} />
      )}
    </>
  );
}
